import React, { useState, useRef, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { setUserPreferences } from '../../../store/userPreferencesSlice';
import { setSelectedModel, setSelectedTool } from '../../../store/chatModeSlice';
import { useTheme } from '../../Theme/ThemeProvider';
import { MODEL_OPTIONS } from '../../../utils/config/modelConfig';
import { useAccount, useMsal } from '@azure/msal-react';
import { USER_BUBBLE_COLORS, setCookie, getSavedBubbleColor } from '../../../utils/user/bubbleColors';
import { createUserPreferencesService } from '../../../utils/user/userPreferences.service';
import { getUserInitials } from '../../../utils/user/userUtils';

const FAVORITABLE_MODEL_IDS = ['gpt-5.5-fast', 'gpt-5.5-reasoning', 'claude-sonnet-5.0-fast', 'claude-opus-4.8-reasoning'];

type AppearanceOption = 'system' | 'light' | 'dark';

interface PersonalizationTabProps {
  onClose: () => void;
  scrollToSection?: string;
}

export function PersonalizationTab({ onClose, scrollToSection }: PersonalizationTabProps) {
  const dispatch = useAppDispatch();
  const followUpQuestions = useAppSelector((state) => state.userPreferences.followUpQuestions);
  const currentModelId = useAppSelector((state) => state.chatMode.selectedModelId);
  const { mode, setMode, isUsingSystemTheme, setSystemTheme, fontSize, setFontSize, fontSizeLabels } = useTheme();
  const account = useAccount();
  const { instance: msalInstance } = useMsal();

  const bubbleColorRef = useRef<HTMLDivElement>(null);
  const [bubbleColorHighlighted, setBubbleColorHighlighted] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Radix Dialog's internal scroll lock (react-remove-scroll) calls preventDefault() on
  // wheel events, preventing trackpad/mouse-wheel scroll inside the dialog. Work around
  // it by directly manipulating scrollTop via a native listener — this bypasses the
  // prevented default without fighting the library's event capture order.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      // deltaMode: 0 = pixels, 1 = lines (~20px each), 2 = pages (~300px each)
      const delta = e.deltaMode === 1 ? e.deltaY * 20 : e.deltaMode === 2 ? e.deltaY * 300 : e.deltaY;
      el.scrollTop += delta;
    };
    el.addEventListener('wheel', handleWheel, { passive: true });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [])

  // Scroll to and highlight bubble color section if requested
  useEffect(() => {
    if (scrollToSection === 'bubble-color' && bubbleColorRef.current) {
      setTimeout(() => {
        bubbleColorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setBubbleColorHighlighted(true);
        setTimeout(() => setBubbleColorHighlighted(false), 1800);
      }, 120);
    }
  }, [scrollToSection]);

  // Determine current appearance value
  const currentAppearance: AppearanceOption = isUsingSystemTheme ? 'system' : mode;

  // Local state for cancel/save pattern
  const [localFollowUp, setLocalFollowUp] = useState(followUpQuestions);
  const [localModelId, setLocalModelId] = useState(
    () => localStorage.getItem('favoriteModelId') || currentModelId
  );
  const [localAppearance, setLocalAppearance] = useState<AppearanceOption>(currentAppearance);
  const [localBubbleColor, setLocalBubbleColor] = useState(() => getSavedBubbleColor());
  const [localFontSize, setLocalFontSize] = useState<0 | 1 | 2>(fontSize as 0 | 1 | 2);

  const savedFavorite = localStorage.getItem('favoriteModelId') || currentModelId;
  const savedBubbleColor = getSavedBubbleColor();
  const hasChanges =
    localFollowUp !== followUpQuestions ||
    localModelId !== savedFavorite ||
    localAppearance !== currentAppearance ||
    localBubbleColor !== savedBubbleColor ||
    localFontSize !== fontSize;

  const handleSave = () => {
    // Save follow-up preference
    if (localFollowUp !== followUpQuestions) {
      dispatch(setUserPreferences({ followUpQuestions: localFollowUp }));
    }

    // Save default model
    if (localModelId !== savedFavorite) {
      localStorage.setItem('favoriteModelId', localModelId);
      dispatch(setSelectedModel(localModelId));
      dispatch(setUserPreferences({ modelPref: localModelId }));
      // Persist to backend so it survives page refresh
      createUserPreferencesService(msalInstance)
        .updateUserPreferences({ modelPref: localModelId })
        .catch(err => console.error('[UserPreferences] Failed to save modelPref:', err));
      // Activate the corresponding tool if needed
      const modelConfig = MODEL_OPTIONS.find(m => m.id === localModelId);
      if (modelConfig?.activatesTool) {
        dispatch(setSelectedTool(modelConfig.activatesTool));
      } else {
        dispatch(setSelectedTool(null));
      }
    }

    // Save appearance
    if (localAppearance !== currentAppearance) {
      if (localAppearance === 'system') {
        setSystemTheme();
      } else {
        setMode(localAppearance);
      }
    }

    // Save bubble color
    if (localBubbleColor !== savedBubbleColor) {
      localStorage.setItem('user-bubble-color', localBubbleColor);
      setCookie('user-bubble-color', localBubbleColor);
      window.dispatchEvent(new Event('user-bubble-color-change'));
      const colorName = USER_BUBBLE_COLORS.find(c => c.value === localBubbleColor)?.name ?? localBubbleColor;
      createUserPreferencesService(msalInstance)
        .updateUserPreferences({ bubbleColor: colorName })
        .catch(err => console.error('[UserPreferences] Failed to save bubbleColor:', err));
    }

    // Save font size
    if (localFontSize !== fontSize) {
      setFontSize(localFontSize);
      const label = fontSizeLabels[localFontSize];
      createUserPreferencesService(msalInstance)
        .updateUserPreferences({ fontSize: label })
        .catch(err => console.error('[UserPreferences] Failed to save fontSize:', err));
    }

    onClose();
  };

  const handleCancel = () => {
    setLocalFollowUp(followUpQuestions);
    setLocalModelId(savedFavorite);
    setLocalAppearance(currentAppearance);
    setLocalBubbleColor(getSavedBubbleColor());
    setLocalFontSize(fontSize as 0 | 1 | 2);
    onClose();
  };

  const favoritableModels = MODEL_OPTIONS.filter(m => FAVORITABLE_MODEL_IDS.includes(m.id));

  const appearanceOptions: { value: AppearanceOption; label: string }[] = [
    { value: 'system', label: 'System' },
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto chat-page-scroll space-y-6 pr-4">
        {/* Default AI Model */}
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
              Default AI Model
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text)', opacity: 0.6 }}>
              Set your preferred AI model for conversations
            </p>
          </div>
          <select
            value={localModelId}
            onChange={(e) => setLocalModelId(e.target.value)}
            className="text-sm rounded-md px-3 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1"
            style={{
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border)',
              width: 160,
            }}
          >
            {favoritableModels.map((model) => (
              <option key={model.id} value={model.id}>
                {model.shortName || model.displayName}
              </option>
            ))}
          </select>
        </div>

        {/* Appearance */}
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
              Appearance
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text)', opacity: 0.6 }}>
              Customize theme, colors, and font size
            </p>
          </div>
          <select
            value={localAppearance}
            onChange={(e) => setLocalAppearance(e.target.value as AppearanceOption)}
            className="text-sm rounded-md px-3 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1"
            style={{
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border)',
              width: 160,
            }}
          >
            {appearanceOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Language (placeholder) */}
        {/* <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
              Language
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text)', opacity: 0.6 }}>
              Set your preferred language for the interface
            </p>
          </div>
          <select
            disabled
            className="text-sm rounded-md px-3 py-1.5 cursor-not-allowed opacity-50"
            style={{
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border)',
              width: 160,
            }}
          >
            <option>Auto Detect</option>
          </select>
        </div> */}

        {/* Follow-up Questions */}
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
              Follow-up Question/Suggestion
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text)', opacity: 0.6 }}>
              Show suggested follow-up questions after AI responses
            </p>
          </div>
          <div style={{ minWidth: 160, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <button
            type="button"
            role="switch"
            aria-label="Toggle follow-up suggestions"
            aria-checked={localFollowUp}
            onClick={() => setLocalFollowUp(!localFollowUp)}
            className="relative shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              width: 36,
              height: 18,
              borderRadius: 9,
              backgroundColor: localFollowUp ? '#2664D3' : 'var(--color-border)',
            }}
          >
            <span
              className="pointer-events-none inline-block rounded-full bg-white shadow-sm transition-all duration-200 ease-in-out"
              style={{
                width: 14,
                height: 14,
                position: 'absolute',
                top: 2,
                left: localFollowUp ? 20 : 2,
              }}
            />
          </button>
          </div>
        </div>

        {/* Font Size */}
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>Font Size</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text)', opacity: 0.6 }}>Adjust the text size across the interface</p>
          </div>
          <div className="flex rounded-md overflow-hidden shrink-0" style={{ border: '1px solid var(--color-border)' }}>
            {fontSizeLabels.map((label, i) => (
              <button
                key={label}
                type="button"
                onClick={() => setLocalFontSize(i as 0 | 1 | 2)}
                className="text-xs font-medium transition-colors"
                style={{
                  padding: '6px 10px',
                  background: localFontSize === i ? '#2664D3' : 'var(--color-surface)',
                  color: localFontSize === i ? '#ffffff' : 'var(--color-text)',
                  borderRight: i < fontSizeLabels.length - 1 ? '1px solid var(--color-border)' : 'none',
                  fontWeight: localFontSize === i ? 600 : 400,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Chat Bubble Color */}
        <div
          ref={bubbleColorRef}
          style={{
            borderRadius: 8,
            transition: 'outline-color 0.3s ease, background 0.3s ease',
            outline: bubbleColorHighlighted ? '2px solid #2664D3' : '2px solid transparent',
            outlineOffset: 4,
            background: bubbleColorHighlighted ? 'rgba(38,100,211,0.07)' : 'transparent',
          }}
        >
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex-1 mr-4">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>Chat Bubble Color</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text)', opacity: 0.6 }}>Customize the color of your chat messages</p>
            </div>
            {/* Preview bubble */}
            {(() => {
              const isDefault = localBubbleColor === 'default';
              const meta = USER_BUBBLE_COLORS.find(c => c.value === localBubbleColor);
              const tone = meta?.textTone || 'light';
              const forceDark = !isDefault && (tone === 'dark' || (tone === 'auto' && mode === 'light'));
              return (
                <div
                  className="w-7 h-7 rounded-full border-2 flex items-center justify-center text-[10px] font-bold shrink-0"
                  style={{
                    background: isDefault ? (mode === 'dark' ? '#313136' : '#e5e7eb') : localBubbleColor,
                    borderColor: 'var(--color-border)',
                    color: isDefault ? (mode === 'dark' ? '#ffffff' : '#1f2937') : (forceDark ? '#1f2937' : '#ffffff'),
                  }}
                >
                  {getUserInitials(account?.name)}
                </div>
              );
            })()}
          </div>
          {/* Color swatches */}
          <div className="flex flex-wrap gap-1.5 pb-1 pl-0.5">
            {USER_BUBBLE_COLORS.map((color, index) => {
              const tone = color.textTone || 'light';
              const forceDark = tone === 'dark' || (tone === 'auto' && mode === 'light');
              const isSelected = localBubbleColor === color.value;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => setLocalBubbleColor(color.value)}
                  title={color.name}
                  aria-label={`Select ${color.name} color`}
                  className="w-6 h-6 rounded-full border-2 transition-all duration-150 hover:scale-110 flex items-center justify-center"
                  style={{
                    background: color.value === 'default' ? (mode === 'dark' ? '#313136' : '#e5e7eb') : color.value,
                    borderColor: isSelected ? (mode === 'dark' && tone === 'dark' ? '#626972' : 'var(--color-text)') : 'var(--color-border)',
                    boxShadow: isSelected ? (mode === 'dark' && tone === 'dark' ? '0 0 0 1px #626972' : '0 0 0 1px var(--color-text)') : 'none',
                    color: color.value === 'default' ? (mode === 'dark' ? '#ffffff' : '#1f2937') : (forceDark ? '#1f2937' : '#ffffff'),
                  }}
                >
                  {isSelected ? (
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ pointerEvents: 'none' }}>
                      <path d="M2 5.5l2 2L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : color.value === 'default' ? (
                    <span className="text-[9px] font-bold">D</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer with Cancel/Save */}
      <div
        className="flex items-center justify-end gap-3 pt-4 mt-4"
        style={{ borderTop: '1px solid var(--color-border)' }}
      >
        <button
          onClick={handleCancel}
          className="px-4 py-1.5 text-sm rounded-md transition-colors"
          style={{ color: 'var(--color-text)' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-hover)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!hasChanges}
          className="px-4 py-1.5 text-sm font-medium rounded-md transition-colors text-white"
          style={{
            background: '#2664D3',
            opacity: hasChanges ? 1 : 0.4,
            cursor: hasChanges ? 'pointer' : 'not-allowed',
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
}
